/**
 * Visit cockpit domain rules (D4 — visit-cockpit).
 *
 * Pure, dependency-free predicates and date defaults shared by the API
 * service layer and the UI. They carry no persistence and no clock of
 * their own: "now" is always an explicit argument, so tests stay
 * deterministic (rule 30-testing) and the caller owns the system clock.
 *
 * Dates are calendar days as `YYYY-MM-DD`; times are `HH:MM` (or null,
 * which is normal for phone and home visits — REQ-VIS-015).
 */
/** Empty strings from HTML inputs count as "not filled in". */
function normalize(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}
/**
 * REQ-VIS-035: the performed date alone decides the status. Clearing it
 * (REQ-VIS-028) brings the visit back to `scheduled`.
 */
export function visitStatus(performedDate) {
    return normalize(performedDate) === null ? 'scheduled' : 'performed';
}
/**
 * The canonical two-part visit label (mail 6, decision D.5): `Nazwa 1 (Nazwa 2)`
 * when the optional second name is present, otherwise `Nazwa 1` alone. The
 * second name is trimmed, so a blank value never yields a dangling `()`.
 */
export function visitLabel(visitName1, visitName2) {
    const second = visitName2 === null ? '' : visitName2.trim();
    return second.length > 0 ? `${visitName1} (${second})` : visitName1;
}
/**
 * REQ-VIS-017: the registration button is highlighted only for a scheduled,
 * not-yet-registered visit whose booked date is today or earlier. A missing
 * booked date or an existing performed date never qualifies.
 */
export function isReadyForRegistration(planned, performed, today) {
    if (visitStatus(performed.performedDate) === 'performed') {
        return false;
    }
    const scheduledDate = normalize(planned.scheduledDate);
    if (scheduledDate === null) {
        return false;
    }
    return scheduledDate <= today;
}
/**
 * REQ-VIS-022 ("teraz"): fill the performed date and time from the system
 * clock. The caller passes the clock in; the values stay editable afterwards.
 */
export function nowAsRegistration(now) {
    return {
        performedDate: normalize(now.date),
        performedTime: normalize(now.time),
    };
}
/**
 * REQ-VIS-023: copy the booked date and time over to the performed side.
 * Available only when a booked date exists; an empty booked time stays empty
 * (a phone visit does not silently become a timed visit).
 */
export function copyScheduleToPerformed(planned) {
    const scheduledDate = normalize(planned.scheduledDate);
    if (scheduledDate === null) {
        return { performedDate: null, performedTime: null };
    }
    return {
        performedDate: scheduledDate,
        performedTime: normalize(planned.scheduledTime),
    };
}
/**
 * REQ-VIS-044: default date for a procedure ticked without one —
 * the performed date if the visit already has it, otherwise the booked date,
 * otherwise today. An existing procedure date is never overwritten; that
 * guard belongs to the caller / service layer.
 */
export function defaultProcedureDate(planned, performed, today) {
    return (normalize(performed.performedDate) ??
        normalize(planned.scheduledDate) ??
        normalize(today));
}
/**
 * REQ-VIS-031: white when the visit uses no kits, green when every planned
 * kit is registered, red otherwise (fewer registered than planned). The flag
 * is independent of the visit status — an unregistered visit can already be red.
 */
export function kitFlag(registeredCount, expectedCount) {
    if (expectedCount <= 0) {
        return 'white';
    }
    return registeredCount >= expectedCount ? 'green' : 'red';
}
/**
 * REQ-VIS-031: the automatic colour unless the user overrode it by hand.
 * A manual choice always wins, including a manual "green" on incomplete data.
 */
export function resolveKitFlag(choice, registeredCount, expectedCount) {
    return choice === 'auto' ? kitFlag(registeredCount, expectedCount) : choice;
}
/**
 * REQ-VIS-048: only lines added by hand outside the template may be deleted.
 * Template lines are ticked off instead, never removed.
 */
export function isProcedureRemovable(source) {
    return source === 'outside';
}
/**
 * REQ-VIS-047: an automatic/conditional procedure is ticked by default when
 * its conditions are met, but a manual untick is remembered — it is never
 * ticked again automatically. Callers pass `tickedByUser` from the saved draft.
 */
export function isProcedureAutoChecked(autoConditionsMet, manuallyUnticked) {
    if (manuallyUnticked) {
        return false;
    }
    return autoConditionsMet;
}
/** A positive multiplicity; a missing or nonsensical one counts as a single line. */
function multiplicity(count) {
    return Number.isFinite(count) && count >= 1 ? Math.floor(count) : 1;
}
/**
 * REQ-VIS-045: the worked list joins the lines already saved on this visit
 * with the template positions that are still missing, and restores the
 * template multiplicity (a procedure planned three times keeps three lines,
 * a partly saved multiset is topped back up to three). Saved lines come
 * first, in their own order — the same order as the template comes from the
 * caller, not from this function; new positions are appended in template
 * order. Nothing is ever dropped: a line saved on this visit stays on the
 * list even when the template no longer knows it.
 */
export function buildProcedureWorkList(saved, template) {
    const lines = saved.map((line) => ({
        procedureKey: line.procedureKey,
        key: line.key,
        templateKey: line.templateKey,
        removable: isProcedureRemovable(line.templateKey === null ? 'outside' : 'template'),
    }));
    for (const position of template) {
        const present = saved.filter((line) => line.procedureKey === position.procedureKey).length;
        const missing = multiplicity(position.count) - present;
        for (let index = 0; index < missing; index += 1) {
            lines.push({
                procedureKey: position.procedureKey,
                key: null,
                templateKey: position.templateKey,
                removable: false,
            });
        }
    }
    return lines;
}
/**
 * REQ-VIS-045 step 6 / REQ-VIS-049: saving never deletes. A line that already
 * has a record is updated in place — that is what keeps an unticked line as
 * "not performed" instead of losing it, so it can still be traced later. A
 * line without a record is inserted.
 */
export function procedureWrites(lines) {
    return lines.map((line) => ({
        procedureKey: line.procedureKey,
        key: line.key,
        action: line.key === null ? 'insert' : 'update',
    }));
}
//# sourceMappingURL=visits.js.map